import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  createRateK2,
  updateRateK2,
  type RateK2Input,
  type RateK2Patch,
} from '@/api/tariffs'
import type { RateK2 } from '@/types/tariffs'
import { Button } from '@/components/ui/Button'
import { PillToggle } from '@/components/ui/PillToggle'
import { Input } from '@/components/ui/Input'
import { CompanyStatusBadge } from '@/components/companies/CompanyStatusBadge'
import { InlineNumberInput } from './InlineNumberInput'

/**
 * §6.3 TARIFFS_SPEC.md — К2 (диапазоны оценочной цены).
 *
 * Валидация целостности активных диапазонов (отсутствие пересечений и дыр)
 * на этом этапе НЕ выполняется — администратор сам отвечает за консистентность
 * справочника. Проверяется только форма отдельной строки (`range_from >= 0`,
 * `range_to > range_from` или ∞, `coefficient > 0`).
 */
export function RatesK2Table({ rows }: { rows: RateK2[] }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [showInactive, setShowInactive] = useState(false)
  const [adding, setAdding] = useState(false)
  const [addFrom, setAddFrom] = useState('')
  const [addTo, setAddTo] = useState('')
  const [addCoeff, setAddCoeff] = useState('')
  const [addError, setAddError] = useState<string | null>(null)
  const [rowError, setRowError] = useState<Record<string, string>>({})

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: RateK2Patch }) => updateRateK2(id, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ['tariffs', 'rates-k2'] })
      const prev = qc.getQueryData<RateK2[]>(['tariffs', 'rates-k2'])
      if (prev) {
        qc.setQueryData<RateK2[]>(
          ['tariffs', 'rates-k2'],
          prev.map((r) => (r.id === id ? { ...r, ...patch } : r)),
        )
      }
      return { prev }
    },
    onError: (err, vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['tariffs', 'rates-k2'], ctx.prev)
      setRowError((s) => ({
        ...s,
        [vars.id]: err instanceof Error ? err.message : t('tariffs.errorGeneric'),
      }))
    },
    onSuccess: (_d, vars) => {
      setRowError((s) => {
        if (!s[vars.id]) return s
        const next = { ...s }
        delete next[vars.id]
        return next
      })
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['tariffs', 'rates-k2'] })
    },
  })

  const createMut = useMutation({
    mutationFn: (input: RateK2Input) => createRateK2(input),
    onSuccess: () => {
      setAdding(false)
      setAddFrom('')
      setAddTo('')
      setAddCoeff('')
      setAddError(null)
      void qc.invalidateQueries({ queryKey: ['tariffs', 'rates-k2'] })
    },
    onError: (err) => {
      setAddError(err instanceof Error ? err.message : t('tariffs.k2.addFailed'))
    },
  })

  const visible = rows
    .filter((r) => showInactive || r.is_active)
    .sort((a, b) => a.range_from - b.range_from)

  function submitAdd() {
    setAddError(null)
    const from = Number(addFrom.replace(',', '.'))
    const to = addTo.trim() === '' ? null : Number(addTo.replace(',', '.'))
    const coeff = Number(addCoeff.replace(',', '.'))
    if (!Number.isFinite(from) || from < 0) {
      setAddError(t('tariffs.k2.errFrom'))
      return
    }
    if (to != null && (!Number.isFinite(to) || to <= from)) {
      setAddError(t('tariffs.k2.errTo'))
      return
    }
    if (!Number.isFinite(coeff) || coeff <= 0) {
      setAddError(t('tariffs.k1.errCoeff'))
      return
    }
    createMut.mutate({
      range_from: from,
      range_to: to,
      coefficient: coeff,
      is_active: true,
    })
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PillToggle
          checked={showInactive}
          onChange={setShowInactive}
          label={t('tariffs.showInactive')}
        />
        <Button
          variant="primary"
          onClick={() => setAdding((v) => !v)}
          aria-expanded={adding}
        >
          {adding ? t('tariffs.hideForm') : t('tariffs.k2.addRange')}
        </Button>
      </div>

      {adding ? (
        <div className="rounded-xl border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] p-3">
          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
            <Input
              label={t('tariffs.k2.colFrom')}
              value={addFrom}
              onChange={(e) => setAddFrom(e.target.value)}
              placeholder="0"
              inputMode="decimal"
            />
            <Input
              label={t('tariffs.k2.toFieldLabel')}
              value={addTo}
              onChange={(e) => setAddTo(e.target.value)}
              placeholder="—"
              inputMode="decimal"
            />
            <Input
              label={t('tariffs.coefficient')}
              value={addCoeff}
              onChange={(e) => setAddCoeff(e.target.value)}
              placeholder="1.0"
              inputMode="decimal"
            />
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={submitAdd}
                disabled={createMut.isPending}
              >
                {createMut.isPending ? t('tariffs.adding') : t('tariffs.add')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setAdding(false)
                  setAddFrom('')
                  setAddTo('')
                  setAddCoeff('')
                  setAddError(null)
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
          {addError ? <p className="stryzh-input-error-text mt-2">{addError}</p> : null}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-[color:var(--stryzh-color-border-default)]">
        <table className="w-full text-left text-sm">
          <thead className="bg-[color:var(--stryzh-color-surface)] text-[color:var(--stryzh-color-text-secondary)]">
            <tr>
              <th className="px-3 py-2">{t('tariffs.k2.colFrom')}</th>
              <th className="px-3 py-2">{t('tariffs.k2.colTo')}</th>
              <th className="px-3 py-2">К2</th>
              <th className="px-3 py-2">{t('tariffs.colStatus')}</th>
              <th className="px-3 py-2 text-right">{t('tariffs.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-6 text-center text-[color:var(--stryzh-color-text-secondary)]"
                >
                  {t('tariffs.noRecords')}
                </td>
              </tr>
            ) : (
              visible.map((r) => {
                const inactive = !r.is_active
                return (
                  <tr
                    key={r.id}
                    className={`border-t border-[color:var(--stryzh-color-border-default)] ${inactive ? 'opacity-60' : ''}`}
                  >
                    <td className="px-3 py-2">
                      <InlineNumberInput
                        value={r.range_from}
                        ariaLabel={t('tariffs.k2.ariaFrom', { id: r.id })}
                        width={96}
                        min={0}
                        onCommit={(v) => {
                          if (v != null) updateMut.mutate({ id: r.id, patch: { range_from: v } })
                        }}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <InlineNumberInput
                        value={r.range_to}
                        ariaLabel={t('tariffs.k2.ariaTo', { id: r.id })}
                        width={96}
                        allowNullable
                        placeholder="∞"
                        onCommit={(v) =>
                          updateMut.mutate({ id: r.id, patch: { range_to: v } })
                        }
                      />
                    </td>
                    <td className="px-3 py-2">
                      <InlineNumberInput
                        value={r.coefficient}
                        ariaLabel={t('tariffs.k2.ariaCoeff', { id: r.id })}
                        width={96}
                        onCommit={(v) => {
                          if (v != null) updateMut.mutate({ id: r.id, patch: { coefficient: v } })
                        }}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <CompanyStatusBadge isActive={r.is_active} compact />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant={r.is_active ? 'secondary' : 'primary'}
                        onClick={() =>
                          updateMut.mutate({ id: r.id, patch: { is_active: !r.is_active } })
                        }
                      >
                        {r.is_active ? t('tariffs.deactivate') : t('tariffs.activate')}
                      </Button>
                      {rowError[r.id] ? (
                        <div className="stryzh-input-error-text mt-1">{rowError[r.id]}</div>
                      ) : null}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
